import {
  Button,
  Checkbox,
  Col,
  Dropdown,
  Input,
  Row,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import {
  EllipsisOutlined,
  FileExcelOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import {useTranslation} from "react-i18next";
import {getScopes} from "../../helperComms";
import {IMonitoring} from "../../models";
import useFetch from "../../../../../hooks/useFetch";
import useMessageUser from "../../../../../hooks/useMessageUser";
import useExport from "../../../../../hooks/comms/useExport";
import {FetchApiInterface} from "../../../../../interfaces/FetchApi.interface";
import {ResponseInterface} from "../../../../../interfaces/Response.interface";
import FilterModal from "../../../../Components/FilterModal";
import PaginatedTable from "../../components/PaginatedTable";

const { Search } = Input;
const { Title } = Typography;

function MonitoringTable() {
  const { t } = useTranslation();
  const scopes = getScopes();

  const initialColumns = [
    {
      title: t('communityWorker'),
      dataIndex: 'assCommunityWorker',
      key: 'assCommunityWorker',
      visible: true,
    },
    {
      title: t('pu'),
      dataIndex: 'assPuCode',
      key: 'assPuCode',
      visible: true,
    },
    {
      title: t('year'),
      dataIndex: 'year',
      key: 'year',
      visible: true,
    },
    {
      title: t('month'),
      dataIndex: 'month',
      key: 'month',
      visible: true,
    },
    {
      title: t('total'),
      dataIndex: 'total',
      key: 'total',
      visible: true,
    },
    {
      title: t('updated'),
      dataIndex: 'actualizados',
      key: 'actualizados',
      visible: true,
    },
    {
      title: t('pending'),
      dataIndex: 'pendientes',
      key: 'pendientes',
      visible: true,
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [dataSource, setDataSource] = useState<IMonitoring[]>([]);
  const [, setFilterApply] = useState<any>({});
  const [dataFilters, setDataFilters] = useState<any>({});
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const { fetchApi } = useFetch();
  const { showMessage } = useMessageUser();
  const { exportExcel, downloading } = useExport();

  useEffect(() => {
    if (!searchText) {
      const newFilterApply = { ...dataFilters };
      delete newFilterApply.search;
      setDataFilters(newFilterApply);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    getTabla();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFilters]);

  const getTabla = (page = 1, size = pagination.pageSize) => {
    const dataSend: FetchApiInterface = {
      url: `/comms/get_monitoreo?page=${page - 1}&size=${size}`,
      data: { ...dataFilters },
    };
    fetchApi(dataSend)
        .then(({ data }: AxiosResponse<ResponseInterface>) => {
          if (data.status === 'success') {
            const dataResponse = data?.data;
            setDataSource(dataResponse?.content || []);
            setPagination({
              current: dataResponse?.pageable?.pageNumber + 1,
              pageSize: size,
              total: dataResponse?.totalElements,
            });
          } else {
            showMessage(data?.message, 'info');
          }
        })
        .catch((reason: any) => {
          showMessage(reason?.message, 'error');
        });
  };

  const exportData = async () => {
    await exportExcel('/comms/donwload_excel_monitoreo', dataFilters);
  };

  const handleSearch = (value: string) => {
    if (value) {
      setSearchText(value);
      setDataFilters((prev: any) => ({ ...prev, search: value }));
    }
  };

  const visibleColumns = columns.filter(column => column.visible);

  const menu = {
    items: columns.map((column) => ({
      key: column.key,
      label: (
          <Checkbox
              checked={column.visible}
              onChange={(e) => handleVisibleChange(e, column.key)}
          >
            {column.title}
          </Checkbox>
      ),
    })),
  };

  const dropdown = (
      <Dropdown trigger={['click']} menu={menu}>
        <Button type='primary' shape='circle' icon={<EllipsisOutlined />} />
      </Dropdown>
  );

  const handleVisibleChange = (e: any, columnName: string) => {
    const newColumns = columns.map((column) =>
        column.key === columnName ? { ...column, visible: e.target.checked } : column
    );
    setColumns(newColumns);
  };

  const handlePaginationChange = (page: number, pageSize?: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    getTabla(page, pageSize || pagination.pageSize);
  };

  return (
      <Row gutter={[20, 20]} className='p-1'>
        <Col span={24}>
          <Title level={5}>{t('sciScuUpdateMonitoring')}</Title>
        </Col>
        <Col span={24}>
          <Row justify='space-between'>
            <Col>
              <Space>
                <FilterModal
                    filters={dataFilters}
                    setFilters={setDataFilters}
                    setFilterApply={setFilterApply}
                    excludeFilters={['genre', 'statusId', 'rangeInterviewDate', 'rangeUpdatedAt', 'withPhotos', 'lastDays', 'puCode', 'dpa', 'interviewer']}
                />
                {scopes.includes('MON_DESCARGAR') && (
                    <Button
                        shape='round'
                        type='primary'
                        icon={<FileExcelOutlined />}
                        loading={downloading}
                        onClick={exportData}
                    >
                      {t('exportExcel')}
                    </Button>
                )}
              </Space>
            </Col>
            <Col>
              <Space>
                <Search
                    placeholder={t('searchPlaceholder')}
                    onSearch={handleSearch}
                    allowClear
                    enterButton
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />
                {dropdown}
                <Tooltip title={t('reloadListTooltip')}>
                  <Button icon={<SyncOutlined />} onClick={() => getTabla(0)} />
                </Tooltip>
              </Space>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <PaginatedTable
              dataSource={dataSource}
              columns={visibleColumns}
              rowKey='id'
              total={pagination.total}
              current={pagination.current}
              pageSize={pagination.pageSize}
              onPaginationChange={handlePaginationChange}
              onPageSizeChange={handlePaginationChange}
          />
        </Col>
      </Row>
  );
}

export default MonitoringTable;
